import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import { createScopeForm } from '@shared/api/scope-form';
import type { ScopeFormResponse, Uuid } from '@shared/types/api';

type Props = {
  open: boolean;
  onClose: () => void;
  transactionId: Uuid;
  initialScopeForm?: ScopeFormResponse | null;
  onSaved?: () => void;
};

export type ScopeFormValues = {
  serviceDescription: string;
  workScope: string;
  deliverables: string;
  professionalFeeRupees: string;
  expectedTat: string;
  governmentFeeRupees: string;
  additionalRequirements: string;
  exclusions: string;
  specialConditions: string;
};

export function ScopeFormDialog({
  open,
  onClose,
  transactionId,
  initialScopeForm,
  onSaved,
}: Props) {
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ScopeFormValues>({
    mode: 'onChange',
    defaultValues: {
      serviceDescription: initialScopeForm?.serviceDescription || '',
      workScope: initialScopeForm?.workScope || '',
      deliverables: initialScopeForm?.deliverables || '',
      professionalFeeRupees: initialScopeForm?.professionalFeeRupees || '',
      expectedTat: initialScopeForm?.expectedTat || '',
      governmentFeeRupees: initialScopeForm?.governmentFeeRupees || '',
      additionalRequirements: initialScopeForm?.additionalRequirements || '',
      exclusions: initialScopeForm?.exclusions || '',
      specialConditions: initialScopeForm?.specialConditions || '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        serviceDescription: initialScopeForm?.serviceDescription || '',
        workScope: initialScopeForm?.workScope || '',
        deliverables: initialScopeForm?.deliverables || '',
        professionalFeeRupees: initialScopeForm?.professionalFeeRupees || '',
        expectedTat: initialScopeForm?.expectedTat || '',
        governmentFeeRupees: initialScopeForm?.governmentFeeRupees || '',
        additionalRequirements: initialScopeForm?.additionalRequirements || '',
        exclusions: initialScopeForm?.exclusions || '',
        specialConditions: initialScopeForm?.specialConditions || '',
      });
    }
  }, [open, initialScopeForm, reset]);

  const submit = useMutation({
    mutationFn: (values: ScopeFormValues) =>
      createScopeForm(transactionId, {
        serviceDescription: values.serviceDescription.trim(),
        workScope: values.workScope.trim(),
        deliverables: values.deliverables.trim(),
        professionalFeeRupees: values.professionalFeeRupees.trim(),
        expectedTat: values.expectedTat.trim(),
        governmentFeeRupees: values.governmentFeeRupees.trim() || undefined,
        additionalRequirements: values.additionalRequirements.trim() || undefined,
        exclusions: values.exclusions.trim() || undefined,
        specialConditions: values.specialConditions.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scope-form', transactionId] });
      qc.invalidateQueries({ queryKey: ['transaction', transactionId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['chat', transactionId, 'history'] });
      onSaved?.();
      onClose();
    },
  });

  const onSubmit = (values: ScopeFormValues) => {
    submit.mutate(values);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (submit.isPending) return;
        onClose();
      }}
      title={
        initialScopeForm ? 'Edit Scope & Fee Proposal Form' : 'Create Scope & Fee Proposal Form'
      }
      className="max-w-xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submit.isPending}>
            Cancel
          </Button>
          <Button disabled={!isValid || submit.isPending} onClick={handleSubmit(onSubmit)}>
            {submit.isPending ? 'Saving…' : 'Save & Send to Admin'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 text-sm">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Service Description *
          </label>
          <textarea
            {...register('serviceDescription', {
              required: 'Service Description is required',
              validate: (val) => val.trim().length > 0 || 'Service Description is required',
            })}
            placeholder="Detailed description of the service to be performed..."
            rows={2}
            className="w-full rounded-md border border-gray-300 p-2 text-xs focus:border-brand-600 focus:outline-none"
          />
          {errors.serviceDescription && (
            <span className="text-[10px] text-red-500">{errors.serviceDescription.message}</span>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Work Scope *</label>
          <textarea
            {...register('workScope', {
              required: 'Work Scope is required',
              validate: (val) => val.trim().length > 0 || 'Work Scope is required',
            })}
            placeholder="Step-by-step scope of work to be executed..."
            rows={3}
            className="w-full rounded-md border border-gray-300 p-2 text-xs focus:border-brand-600 focus:outline-none"
          />
          {errors.workScope && (
            <span className="text-[10px] text-red-500">{errors.workScope.message}</span>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Deliverables *</label>
          <textarea
            {...register('deliverables', {
              required: 'Deliverables are required',
              validate: (val) => val.trim().length > 0 || 'Deliverables are required',
            })}
            placeholder="List final deliverables (e.g. Approved GST Registration Certificate, Filing Copy)..."
            rows={2}
            className="w-full rounded-md border border-gray-300 p-2 text-xs focus:border-brand-600 focus:outline-none"
          />
          {errors.deliverables && (
            <span className="text-[10px] text-red-500">{errors.deliverables.message}</span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Professional Fee (₹) *
            </label>
            <Input
              inputMode="decimal"
              {...register('professionalFeeRupees', {
                required: 'Professional Fee is required',
                validate: (val) =>
                  (!isNaN(Number(val)) && Number(val) > 0) || 'Enter a valid amount',
              })}
              placeholder="e.g. 2500.00"
            />
            {errors.professionalFeeRupees && (
              <span className="text-[10px] text-red-500">
                {errors.professionalFeeRupees.message}
              </span>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Expected TAT *</label>
            <Input
              {...register('expectedTat', {
                required: 'Expected TAT is required',
                validate: (val) => val.trim().length > 0 || 'Expected TAT is required',
              })}
              placeholder="e.g. 3-5 business days"
            />
            {errors.expectedTat && (
              <span className="text-[10px] text-red-500">{errors.expectedTat.message}</span>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Government / Statutory Charges (₹) (if applicable)
          </label>
          <Input
            inputMode="decimal"
            {...register('governmentFeeRupees')}
            placeholder="e.g. 1000.00"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Additional Requirements (Optional)
          </label>
          <textarea
            {...register('additionalRequirements')}
            placeholder="Any specific documents or client inputs needed..."
            rows={2}
            className="w-full rounded-md border border-gray-300 p-2 text-xs focus:border-brand-600 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Exclusions (Optional)
          </label>
          <textarea
            {...register('exclusions')}
            placeholder="Work not included in this proposal..."
            rows={2}
            className="w-full rounded-md border border-gray-300 p-2 text-xs focus:border-brand-600 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Special Conditions (Optional)
          </label>
          <textarea
            {...register('specialConditions')}
            placeholder="Any special terms or conditions..."
            rows={2}
            className="w-full rounded-md border border-gray-300 p-2 text-xs focus:border-brand-600 focus:outline-none"
          />
        </div>

        {submit.isError ? (
          <p className="text-sm text-red-600">{apiErrorMessage(submit.error)}</p>
        ) : null}
      </form>
    </Dialog>
  );
}
